import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   DoCheck,
   Input,
   OnDestroy,
   OnInit,
   ViewEncapsulation,
} from '@angular/core';
import {
   AbstractControl,
   ControlContainer,
   FormGroupDirective,
   UntypedFormControl,
   UntypedFormGroup,
   Validators,
} from '@angular/forms';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CallBackTypes, ExtensionTypes, NullActionTypes, PromptTypes, QueueStrategies } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { copyNameControlToShortNameAndDisplayName } from 'src/app/common-components/helpers/extension-form-helper';
import { createPromptSuccessNoRouteToDetail } from 'src/app/pages/add-prompt/state/add-prompt.actions';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { addSpacesToTypeName } from 'src/app/pipes/split-enum-strings/add-spaces.pipe';
import { getNullActionPlaceholder, setNullActionValue } from '../../helpers/null-action-helper';
import { ExtensionTypeDetails } from '../../models/extension-type-details';
import { GetExtensionDetail_extension_extensionQueue } from '../../queries/generated/GetExtensionDetail';
import { ExtensionDetailFormService } from '../../services/extension-detail-form.service';
import { uploadCallbackPrompt, uploadMohPrompt } from '../../state/extension-detail.actions';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-extension-queue-details',
   styleUrls: ['./extension-queue-details.component.scss'],
   templateUrl: './extension-queue-details.component.html',
   standalone: false,
})
export class ExtensionQueueDetailsComponent extends ExtensionTypeDetails implements OnInit, OnDestroy, DoCheck {
   @Input() extensionId: number | null;

   @Input() set canEdit(edit: boolean) {
      if (!edit) {
         this.form?.disable();
      } else {
         this.form?.enable();
      }
      this._canEdit = edit;
   }

   get canEdit(): boolean {
      return this._canEdit;
   }

   private _extensionQueue: GetExtensionDetail_extension_extensionQueue | null;

   private _originalExtensionQueue: GetExtensionDetail_extension_extensionQueue;

   private exitRouteToExtensionControl: AbstractControl | null = null;

   private _canEdit: boolean;

   private destroyed$ = new Subject<void>();

   readonly callBackTypes = CallBackTypes;

   promptsToInclude: number[] = [];

   callbackPromptsToInclude: number[] = [];

   exiteRouteVoicemail: boolean | undefined;

   @Input() extensions: [] | null;

   @Input() extensionGroups: [] | null;

   @Input() get extensionQueue(): GetExtensionDetail_extension_extensionQueue | null {
      return this._extensionQueue;
   }

   set extensionQueue(value: GetExtensionDetail_extension_extensionQueue | null) {
      this._extensionQueue = value;
      if (
         value !== null &&
         value?.holdMusicPrompt?.promptId !== undefined &&
         value?.holdMusicPrompt?.promptId !== null
      ) {
         this.holdMusicOptionValue = 'holdMusicPromptId';
      } else {
         this.holdMusicOptionValue = 'default';
      }

      if (value && value.holdMusicPrompt) {
         this._originalExtensionQueue = value;
      }

      this.isCallBackAvailable = this.extensionQueue?.isCallBackAvailable;

      this.hasPreventEmptyQueue = value?.hasPreventEmptyQueue;

      if (value) {
         this.formService.updateExtensionQueue(value);
      }
      if (value?.exitRouteVoicemailOnly) {
         this.exiteRouteVoicemail = value?.exitRouteVoicemailOnly;
      }
   }

   @Input() accountId: number | null;

   queueStrategies = Object.values(QueueStrategies);

   callbackTypes = Object.values(CallBackTypes);

   form: UntypedFormGroup;

   holdMusicOptionValue = 'default';

   isCallBackAvailable: boolean | undefined;

   hasPreventEmptyQueue: boolean | undefined;

   searchControl = new UntypedFormControl('');

   queueMemberExtensionTypes = [ExtensionTypes.Phone];

   queueEmptyPlaceholder: string;

   nullActions: NullActionTypes[] = [NullActionTypes.Directory, NullActionTypes.Hangup];

   constructor(
      private store: Store,
      public formService: ExtensionDetailFormService,
      private changeDetectorRef: ChangeDetectorRef,
      private controlContainer: ControlContainer,
      private parentForm: FormGroupDirective,
      private actions$: Actions
   ) {
      super();
   }

   ngOnDestroy(): void {
      this.extensionQueue = null;
      this.destroyed$.next();
      this.destroyed$.complete();
   }

   ngOnInit(): void {
      this.form = <UntypedFormGroup>this.controlContainer.control;
      this.exitRouteToExtensionControl = this.form.get('exitRouteToExtension');
      this.initializeFormValues();
      this.actions$.pipe(ofType(createPromptSuccessNoRouteToDetail), takeUntil(this.destroyed$)).subscribe((action) => {
         if (action.promptItem.typeId === PromptTypes.MusicOnHold) {
            this.promptsToInclude.push(action.promptItem.promptId);
            this.form.controls['holdMusicPrompt'].setValue(action.promptItem);
            this.form.controls['holdMusicPrompt'].markAsDirty();
            this.form.controls['holdMusicPrompt'].updateValueAndValidity();
         } else if (action.promptItem.typeId === PromptTypes.QueueCallback) {
            this.callbackPromptsToInclude.push(action.promptItem.promptId);
            this.form.controls['callbackPrompt'].setValue(action.promptItem);
            this.form.controls['callbackPrompt'].markAsDirty();
            this.form.controls['callbackPrompt'].updateValueAndValidity();
         }
      });

      this.exitRouteToExtensionControl?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value: ExtensionOption | null) => {
            this.handleExitRouteExtensionChange(value);
         });

      this.isCallBackAvailable = this.extensionQueue?.isCallBackAvailable;
      const callbackPrompt = this.form.get('callbackPrompt');
      const callersNeededForCallback = this.form.get('callersNeededForCallback');
      const isAnnounceCallbackPrompt = this.form.get('isAnnounceCallback');
      const typeOfCallback = this.form.get('typeOfCallback');
      if (this.canEdit) {
         if (this.isCallBackAvailable) {
            callbackPrompt?.enable();
            callersNeededForCallback?.enable();
            isAnnounceCallbackPrompt?.enable();
            typeOfCallback?.enable();
         } else {
            callbackPrompt?.disable();
            callersNeededForCallback?.disable();
            isAnnounceCallbackPrompt?.disable();
            typeOfCallback?.disable();
         }
      }

      this.checkMinimumCallersFormField();

      this.updateMOHDisabledState();

      const emptyQueueExitExtension = this.parentForm.form.get('extensionQueue.queueEmptyExitRouteToExtension');
      const emptyQueueExitNullAction = this.parentForm.form.get('extensionQueue.queueEmptyExitNullAction');

      this.queueEmptyPlaceholder = getNullActionPlaceholder(emptyQueueExitNullAction?.value);

      typeOfCallback?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe(() => this.checkMinimumCallersFormField());
      emptyQueueExitNullAction?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.queueEmptyPlaceholder = getNullActionPlaceholder(value);
      });
      let lastEmptyQueueRouteValue = { current: null };
      emptyQueueExitExtension?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value: ExtensionOption | null) => {
            setNullActionValue(value, lastEmptyQueueRouteValue, emptyQueueExitExtension, emptyQueueExitNullAction);
            this.queueEmptyPlaceholder = getNullActionPlaceholder(emptyQueueExitNullAction?.value);
         });
      copyNameControlToShortNameAndDisplayName(this.parentForm.form, this.form);
   }

   ngDoCheck(): void {
      if (this.parentForm.form.invalid && (this.parentForm.submitted || this.form.touched)) {
         this.form.markAllAsTouched();
         this.changeDetectorRef.markForCheck();
      }
   }

   private initializeFormValues(): void {
      if (!this.extensionQueue || this.extensionQueue.exitRouteToExtension?.extensionId == null) {
         this.exitRouteToExtensionControl?.setValue(null);
      }
   }

   private handleExitRouteExtensionChange(value: ExtensionOption | null): void {
      const isPhoneExtension = value?.type === ExtensionTypes.Phone;

      if (!isPhoneExtension) {
         this.form.get('exitRouteVoicemailOnly')?.setValue(false, { emitEvent: false });
         this.form.get('exitRouteVoicemailOnly')?.markAsDirty();
      }

      this.changeDetectorRef.markForCheck();
   }

   private savedCallbackState: {
      callbackPrompt: any;
      callersNeededForCallback: number | null;
      isAnnounceCallback: boolean;
      typeOfCallback: CallBackTypes | null;
      holdTimeNeededForCallback: number | null;
   } | null = null;

   queueStrategyDisplayWith(option: QueueStrategies): string {
      return `${addSpacesToTypeName(option) ?? ''}`;
   }

   searchQueueStrategy(searchText: string) {
      this.queueStrategies = Object.values(QueueStrategies).filter((strategy) =>
         strategy.toUpperCase().includes(searchText.toUpperCase())
      );
   }

   resetQueueStrategySearch() {
      this.queueStrategies = Object.values(QueueStrategies);
   }

   callbackTypeDisplayWith(option: CallBackTypes): string {
      return `${addSpacesToTypeName(option) ?? ''}`;
   }

   searchCallbackType(searchText: string) {
      this.callbackTypes = Object.values(CallBackTypes).filter((strategy) =>
         strategy.toUpperCase().includes(searchText.toUpperCase())
      );
   }

   resetCallbackTypeSearch() {
      this.callbackTypes = Object.values(CallBackTypes);
   }

   isExtensionTypePhone(): boolean {
      const extensionType = this.form.value.exitRouteToExtension?.type;
      if (extensionType) {
         return extensionType === ExtensionTypes.Phone;
      }
      return false;
   }

   onHoldMusicOptionChange(event: MatCheckboxChange) {
      this.holdMusicOptionValue = event.checked ? 'default' : 'holdMusicPromptId';

      this.updateMOHDisabledState();
      this.formService.form.get('extensionQueue.holdMusicPrompt')?.markAsDirty();
   }

   onPreventEmptyQueueChange(event: MatCheckboxChange) {
      const routeControl = this.formService.form.get('extensionQueue.queueEmptyExitRouteToExtension');
      const nullActionControl = this.formService.form.get('extensionQueue.queueEmptyExitNullAction');
      const preventControl = this.formService.form.get('extensionQueue.hasPreventEmptyQueue');

      this.hasPreventEmptyQueue = event.checked;
      preventControl?.markAsDirty();

      if (this.hasPreventEmptyQueue) {
         routeControl?.setValidators([Validators.required]);
         nullActionControl?.setValidators([Validators.required]);
      } else {
         routeControl?.clearValidators();
         nullActionControl?.clearValidators();

         routeControl?.setValue(null);
         nullActionControl?.setValue(NullActionTypes.Unset);
         nullActionControl?.markAsDirty();
      }

      routeControl?.updateValueAndValidity();
      nullActionControl?.updateValueAndValidity();
   }

   dispatchUploadMohFile(): void {
      this.store.dispatch(uploadMohPrompt());
   }

   dispatchUploadCallbackFile(): void {
      this.store.dispatch(uploadCallbackPrompt());
   }

   onCallBackAvailableOptionChange(checkbox: MatCheckbox) {
      this.isCallBackAvailable = checkbox.checked;
      this.checkCallbackFormFields();
      this.form.get('isCallBackAvailable')?.markAsDirty();
      this.form.get('callbackPrompt')?.markAsDirty();
      this.form.get('typeOfCallback')?.markAsDirty();
   }

   ngOnChanges(): void {
      if (this.form) {
         this.isCallBackAvailable = this.extensionQueue?.isCallBackAvailable;
         this.checkCallbackFormFields();
         this.updateMOHDisabledState();
      }
   }

   checkCallbackFormFields(): void {
      const callbackPrompt = this.form.get('callbackPrompt');
      const callersNeededForCallback = this.form.get('callersNeededForCallback');
      const isAnnounceCallbackPrompt = this.form.get('isAnnounceCallback');
      const typeOfCallback = this.form.get('typeOfCallback');
      const holdTimeNeededForCallback = this.form.get('holdTimeNeededForCallback');

      if (this.isCallBackAvailable) {
         if (this.savedCallbackState) {
            this.form.patchValue(this.savedCallbackState);
            this.savedCallbackState = null;
         }

         callbackPrompt?.enable();
         isAnnounceCallbackPrompt?.enable();
         typeOfCallback?.enable();
      } else {
         this.savedCallbackState = {
            callbackPrompt: callbackPrompt?.value,
            callersNeededForCallback: callersNeededForCallback?.value,
            isAnnounceCallback: isAnnounceCallbackPrompt?.value,
            typeOfCallback: typeOfCallback?.value,
            holdTimeNeededForCallback: holdTimeNeededForCallback?.value,
         };

         callbackPrompt?.setValue(null);
         callersNeededForCallback?.setValue(null);
         typeOfCallback?.setValue(null);
         holdTimeNeededForCallback?.setValue(null);

         if (isAnnounceCallbackPrompt?.value === true) {
            isAnnounceCallbackPrompt?.setValue(false);
         }

         callbackPrompt?.disable();
         callersNeededForCallback?.disable();
         isAnnounceCallbackPrompt?.disable();
         typeOfCallback?.disable();
         holdTimeNeededForCallback?.disable();
      }
      this.checkMinimumCallersFormField();
   }

   private updateMOHDisabledState() {
      if (this.holdMusicOptionValue === 'holdMusicPromptId') {
         this.form.get('holdMusicPrompt')?.enable();
         this.form.get('holdMusicPrompt')?.setValidators(Validators.required);
         this.form.get('holdMusicPrompt')?.updateValueAndValidity();
         if (this._originalExtensionQueue) {
            this.formService.updateExtensionQueue(this._originalExtensionQueue);
         }
      } else {
         this.form.get('holdMusicPrompt')?.disable();
         this.form.get('holdMusicPrompt')?.setValidators(null);
         this.form.get('holdMusicPrompt')?.updateValueAndValidity();
         this.form.get('holdMusicPrompt')?.setValue(null);
      }
   }

   private checkMinimumCallersFormField(): void {
      const isCallbackEnabled = !!this.form.get('isCallBackAvailable')?.value;
      const callbackType = this.form.get('typeOfCallback')?.value as CallBackTypes | null;
      const enabled = this.canEdit && isCallbackEnabled;

      const callers = this.form.get('callersNeededForCallback');
      const holdTime = this.form.get('holdTimeNeededForCallback');

      if (enabled && callbackType === CallBackTypes.CallersInTheQueue) callers?.enable();
      else callers?.disable();

      if (enabled && callbackType === CallBackTypes.ExpectedHoldTime) holdTime?.enable();
      else holdTime?.disable();

      callers?.updateValueAndValidity();
      holdTime?.updateValueAndValidity();
   }
}
